import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Activity,
  Clock,
  CheckCircle2,
  Flame,
  Sparkles,
  ChevronRight,
  Layers,
  FileText,
  Bug,
  Plus,
  Trash2,
  Edit3,
  X,
  Save,
  Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DEFAULT_PROJECTS = [
  {
    id: "proj_1",
    title: "Monster Black Market DLC 2",
    tag: "Dark RPG Maker • DLC 2 • Full Uncensored",
    status: "Tahap Finishing 🎯",
    statusColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    totalProgress: 82,
    subSteps: [
      { name: "Translasi Teks & Dialog", percent: 95 },
      { name: "Proofreading & Alur Bahasa", percent: 70 },
      { name: "Patch In-game & Bug Testing", percent: 40 },
    ],
    founderNote:
      "Sedang uji kecocokan font Indo dan memastikan tidak ada teks dialog yang kepotong saat scene ending. Target rilis minggu depan!",
    updatedAt: "Update kemarin",
  },
  {
    id: "proj_2",
    title: "Secret Class: Midnight Private Lesson",
    tag: "Visual Novel • Romance / Drama • Full Voice",
    status: "Sedang Dikerjakan ⚡",
    statusColor: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    totalProgress: 38,
    subSteps: [
      { name: "Ekstraksi Skrip & Font", percent: 90 },
      { name: "Translasi Naskah Utama", percent: 25 },
      { name: "Persiapan Gambar & UI", percent: 60 },
    ],
    founderNote:
      "Pemenang polling antusias cultist. Aset font sudah beres di-render, sekarang fokus garap chapter awal.",
    updatedAt: "Update 2 hari lalu",
  },
  {
    id: "proj_3",
    title: "Fallen Maiden & The Cursed Kingdom",
    tag: "Dark Fantasy • RPG Maker • 15+ Jam Gameplay",
    status: "Antrean Berikutnya 📋",
    statusColor: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    totalProgress: 15,
    subSteps: [
      { name: "Riset Engine & Naskah", percent: 45 },
      { name: "Translasi Teks Dasar", percent: 0 },
      { name: "Testing Sistem", percent: 0 },
    ],
    founderNote:
      "Bakal langsung digas setelah patch Monster Black Market DLC 2 resmi rilis ke publik.",
    updatedAt: "Update minggu ini",
  },
];

const STATUS_PRESETS = [
  { label: "Tahap Finishing 🎯", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  { label: "Sedang Dikerjakan ⚡", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  { label: "Antrean Berikutnya 📋", color: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
  { label: "Testing Patch 🛠️", color: "text-rose-400 bg-rose-500/10 border-rose-500/30" },
  { label: "Rilis Hari Ini 🔥", color: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
];

export default function TranslationProgressTracker() {
  const { user } = useAuth();
  const isFounder = user?.role === "admin";

  const [projects, setProjects] = useState(() => {
    try {
      const saved = localStorage.getItem("kyouka_progress_projects_v1");
      return saved ? JSON.parse(saved) : DEFAULT_PROJECTS;
    } catch {
      return DEFAULT_PROJECTS;
    }
  });

  const [expandedId, setExpandedId] = useState(projects[0]?.id || "proj_1");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProjects, setEditingProjects] = useState([]);

  const isCustomLocalProjects = (raw) => {
    if (!raw) return false;
    try {
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr) || arr.length === 0) return false;
      return JSON.stringify(arr) !== JSON.stringify(DEFAULT_PROJECTS);
    } catch {
      return false;
    }
  };

  const saveProjectsLocal = (newProjects) => {
    setProjects(newProjects);
    try {
      localStorage.setItem("kyouka_progress_projects_v1", JSON.stringify(newProjects));
    } catch (e) {
      console.error("Failed to save progress to localStorage", e);
    }
  };

  const saveProjects = async (newProjects) => {
    saveProjectsLocal(newProjects);
    try {
      await api.put("/community/progress", { projects: newProjects });
      localStorage.setItem("kyouka_progress_migrated_v1", "1");
    } catch (e) {
      toast.error("Gagal menyinkronkan progres ke server.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    const syncProgress = async () => {
      try {
        const { data } = await api.get("/community/progress");
        if (cancelled) return;

        const migrated = localStorage.getItem("kyouka_progress_migrated_v1");
        const rawLocal = localStorage.getItem("kyouka_progress_projects_v1");

        if (isFounder && !migrated && isCustomLocalProjects(rawLocal)) {
          const localProjects = JSON.parse(rawLocal);
          await api.put("/community/progress", { projects: localProjects });
          localStorage.setItem("kyouka_progress_migrated_v1", "1");
          if (!cancelled) saveProjectsLocal(localProjects);
          return;
        }

        if (data?.initialized && Array.isArray(data.projects) && data.projects.length > 0) {
          localStorage.setItem("kyouka_progress_migrated_v1", "1");
          if (!showEditModal) {
            saveProjectsLocal(data.projects);
          }
        }
      } catch (err) {
        // silent fallback to localStorage / default
      }
    };

    syncProgress();
    const timer = setInterval(syncProgress, 25000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isFounder, showEditModal]);

  const handleOpenEdit = () => {
    // Clone for editing
    setEditingProjects(JSON.parse(JSON.stringify(projects)));
    setShowEditModal(true);
  };

  const handleUpdateEditingProject = (idx, field, value) => {
    const updated = [...editingProjects];
    updated[idx][field] = value;
    setEditingProjects(updated);
  };

  const handleUpdateSubStep = (projIdx, stepIdx, newPercent) => {
    const updated = [...editingProjects];
    const p = Math.max(0, Math.min(100, Number(newPercent) || 0));
    updated[projIdx].subSteps[stepIdx].percent = p;

    // Auto-calculate totalProgress based on average of subSteps
    const avg = Math.round(
      updated[projIdx].subSteps.reduce((acc, s) => acc + (s.percent || 0), 0) /
        updated[projIdx].subSteps.length
    );
    updated[projIdx].totalProgress = avg;
    setEditingProjects(updated);
  };

  const handleAddProject = () => {
    const newProj = {
      id: "proj_" + Date.now(),
      title: "Proyek Visual Novel / RPG Baru",
      tag: "Visual Novel • Full Voice • PC & Android",
      status: "Sedang Dikerjakan ⚡",
      statusColor: "text-amber-400 bg-amber-500/10 border-amber-500/30",
      totalProgress: 10,
      subSteps: [
        { name: "Translasi Teks & Dialog", percent: 15 },
        { name: "Proofreading & Alur Bahasa", percent: 10 },
        { name: "Patch In-game & Bug Testing", percent: 5 },
      ],
      founderNote: "Proyek baru mulai digarap. Stay tuned buat rilis perdana!",
      updatedAt: "Baru saja",
    };
    setEditingProjects([...editingProjects, newProj]);
  };

  const handleDeleteProject = (idx) => {
    if (editingProjects.length <= 1) {
      toast.info("Minimal sisakan 1 proyek garapan.");
      return;
    }
    const updated = editingProjects.filter((_, i) => i !== idx);
    setEditingProjects(updated);
  };

  const handleSaveAll = (e) => {
    e.preventDefault();
    const finalProjects = editingProjects.map((p) => ({
      ...p,
      updatedAt: "Update hari ini",
    }));
    saveProjects(finalProjects);
    setShowEditModal(false);
    toast.success("Progres garapan Founder berhasil diperbarui! 🎉");
  };

  const handleResetDefault = () => {
    if (window.confirm("Kembalikan daftar garapan ke contoh default?")) {
      saveProjects(DEFAULT_PROJECTS);
      setShowEditModal(false);
      toast.success("Daftar garapan direset ke default.");
    }
  };

  const getSubStepIcon = (idx) => {
    if (idx === 0) return FileText;
    if (idx === 1) return Layers;
    return Bug;
  };

  return (
    <section className="relative overflow-hidden rounded-3xl bg-zinc-950/80 border border-rose-500/30 p-6 sm:p-8 shadow-2xl shadow-rose-950/30 backdrop-blur-xl mb-12">
      {/* Decorative Aura */}
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-rose-600/10 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-amber-600/10 blur-[100px] pointer-events-none" />

      {/* Header */}
      <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-zinc-800/80">
        <div className="space-y-1.5 max-w-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-rose-600/20 to-rose-900/30 border border-rose-500/40 text-rose-300 text-xs font-semibold shadow-inner">
              <Activity className="w-3.5 h-3.5 text-rose-400" />
              <span>Papan Status Garapan</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-900 border border-zinc-700/80 text-zinc-400 text-[11px] font-medium">
              <Clock className="w-3 h-3 text-amber-400" />
              <span>Transparansi Solo Dev 👑</span>
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-white tracking-tight pt-1">
            Progres Translasi Proyek Aktif
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
            Pantau sejauh mana proses terjemahan game yang sedang digarap Kyouka Reimei secara langsung tanpa perlu bolak-balik bertanya.
          </p>
        </div>

        <div className="shrink-0 flex items-center gap-3">
          {/* Founder Quick Edit Button */}
          {isFounder && (
            <button
              type="button"
              onClick={handleOpenEdit}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-600/30 to-rose-600/30 hover:from-amber-600/40 hover:to-rose-600/40 border border-amber-400/50 text-amber-200 text-xs font-bold transition shadow-lg shadow-amber-950/40 active:scale-95"
              data-testid="founder-edit-progress-btn"
            >
              <Edit3 className="w-3.5 h-3.5 text-amber-300" />
              <span>Kelola Progres (Founder)</span>
            </button>
          )}

          <div className="flex items-center gap-2 text-xs text-zinc-400 bg-zinc-900/70 border border-zinc-800 px-3.5 py-2 rounded-xl">
            <Sparkles className="w-4 h-4 text-rose-400" />
            <span>
              <strong className="text-white font-bold">{projects.length}</strong> Proyek Berjalan
            </span>
          </div>
        </div>
      </div>

      {/* Project Cards List */}
      <div className="mt-6 space-y-4">
        {projects.map((proj) => {
          const isExpanded = expandedId === proj.id;

          return (
            <div
              key={proj.id}
              className={`relative overflow-hidden rounded-2xl border transition-all ${
                isExpanded
                  ? "bg-zinc-900/80 border-rose-500/60 shadow-xl shadow-rose-950/30"
                  : "bg-zinc-900/40 hover:bg-zinc-900/70 border-zinc-800 hover:border-zinc-700 cursor-pointer"
              }`}
              onClick={() => {
                if (!isExpanded) setExpandedId(proj.id);
              }}
            >
              <div className="p-4 sm:p-5">
                {/* Title & Status Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm sm:text-base font-bold text-white tracking-tight">
                        {proj.title}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${proj.statusColor || "text-amber-400 bg-amber-500/10 border-amber-500/30"}`}>
                        {proj.status}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400">{proj.tag}</p>
                  </div>

                  {/* Percentage Counter */}
                  <div className="flex items-center gap-3 shrink-0 self-start sm:self-center">
                    <div className="text-right">
                      <span className="text-lg sm:text-xl font-black text-white font-mono">
                        {proj.totalProgress}%
                      </span>
                      <span className="text-[10px] text-zinc-500 block">
                        {proj.updatedAt}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main Overall Progress Bar */}
                <div className="mt-4 w-full bg-zinc-950 rounded-full h-2.5 overflow-hidden border border-zinc-800/80">
                  <div
                    className="h-full bg-gradient-to-r from-rose-600 via-rose-500 to-amber-400 rounded-full transition-all duration-1000 shadow-sm shadow-rose-500/50"
                    style={{ width: `${Math.min(100, Math.max(0, proj.totalProgress))}%` }}
                  />
                </div>

                {/* Sub-progress Breakdown */}
                <div className="mt-5 pt-4 border-t border-zinc-800/70 space-y-3">
                  <div className="text-xs font-bold text-zinc-300 flex items-center justify-between">
                    <span>Rincian Tahapan Pengerjaan:</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId(isExpanded ? null : proj.id);
                      }}
                      className="text-[11px] text-rose-400 hover:text-rose-300 underline font-medium"
                    >
                      {isExpanded ? "Sembunyikan Rincian" : "Lihat Rincian"}
                    </button>
                  </div>

                  {isExpanded && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                        {proj.subSteps.map((step, idx) => {
                          const Icon = getSubStepIcon(idx);
                          const isDone = step.percent === 100;

                          return (
                            <div
                              key={idx}
                              className="p-3 rounded-xl bg-zinc-950/60 border border-zinc-800/80 space-y-1.5"
                            >
                              <div className="flex items-center justify-between text-xs">
                                <span className="flex items-center gap-1.5 text-zinc-300 font-medium">
                                  <Icon className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                                  <span className="truncate">{step.name}</span>
                                </span>
                                <span className="font-mono font-bold text-white shrink-0">
                                  {step.percent}%
                                </span>
                              </div>
                              <div className="w-full bg-zinc-900 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-700 ${
                                    isDone
                                      ? "bg-emerald-500"
                                      : "bg-gradient-to-r from-rose-500 to-amber-500"
                                  }`}
                                  style={{ width: `${Math.min(100, Math.max(0, step.percent))}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Founder Personal Note */}
                      {proj.founderNote && (
                        <div className="mt-3 p-3 rounded-xl bg-rose-950/20 border border-rose-500/20 flex items-start gap-2.5 text-xs text-zinc-300 leading-relaxed">
                          <span className="shrink-0 text-base">👑</span>
                          <div>
                            <strong className="text-rose-300 font-bold block mb-0.5">
                              Catatan Solo Dev:
                            </strong>
                            {proj.founderNote}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Founder Edit Progress Modal */}
      {showEditModal && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md animate-in fade-in overflow-y-auto">
          <div className="relative w-full max-w-3xl h-[88vh] max-h-[640px] flex flex-col rounded-2xl sm:rounded-3xl bg-zinc-950 border border-amber-500/40 shadow-2xl overflow-hidden">
            {/* Modal Header (Fixed / Shrink-0) */}
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-zinc-800 bg-zinc-900/90 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-500/20 border border-amber-500/30">
                  <Activity className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">
                    Kelola Progres Garapan (Founder Panel)
                  </h3>
                  <p className="text-xs text-zinc-400">
                    Perbarui persentase, status, atau tambah game yang sedang lu terjemahkan.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body & Sticky Footer inside Form */}
            <form onSubmit={handleSaveAll} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Scrollable Content Body */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5 min-h-0">
                {editingProjects.map((p, pIdx) => (
                  <div
                    key={p.id || pIdx}
                    className="p-4 sm:p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800 space-y-4"
                  >
                    <div className="flex items-center justify-between pb-3 border-b border-zinc-800/80">
                      <span className="text-xs font-bold text-rose-400 uppercase tracking-wider">
                        Game #{pIdx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteProject(pIdx)}
                        className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg hover:bg-red-500/10 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Hapus Game Ini</span>
                      </button>
                    </div>

                    {/* Title & Tag */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-zinc-300 block mb-1">
                          Judul Game:
                        </label>
                        <input
                          type="text"
                          value={p.title}
                          onChange={(e) => handleUpdateEditingProject(pIdx, "title", e.target.value)}
                          className="w-full px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs focus:outline-none focus:border-rose-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-zinc-300 block mb-1">
                          Tag / Format:
                        </label>
                        <input
                          type="text"
                          value={p.tag}
                          onChange={(e) => handleUpdateEditingProject(pIdx, "tag", e.target.value)}
                          placeholder="Contoh: Dark Fantasy • RPG Maker • 15+ Jam"
                          className="w-full px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs focus:outline-none focus:border-rose-500"
                        />
                      </div>
                    </div>

                    {/* Status preset */}
                    <div>
                      <label className="text-xs font-semibold text-zinc-300 block mb-1.5">
                        Pilih Status Proyek:
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {STATUS_PRESETS.map((preset) => (
                          <button
                            key={preset.label}
                            type="button"
                            onClick={() => {
                              handleUpdateEditingProject(pIdx, "status", preset.label);
                              handleUpdateEditingProject(pIdx, "statusColor", preset.color);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                              p.status === preset.label
                                ? `${preset.color} ring-1 ring-white/20`
                                : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                            }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Sub-steps Sliders */}
                    <div className="space-y-3 pt-2">
                      <label className="text-xs font-bold text-zinc-200 block">
                        Rincian Persentase Progres (Otomatis Menghitung Total):
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {p.subSteps.map((step, sIdx) => (
                          <div
                            key={sIdx}
                            className="p-3 rounded-xl bg-zinc-950 border border-zinc-800/80 space-y-2"
                          >
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-zinc-300 font-medium truncate">
                                {step.name}
                              </span>
                              <span className="font-mono font-bold text-amber-300">
                                {step.percent}%
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={step.percent}
                              onChange={(e) => handleUpdateSubStep(pIdx, sIdx, e.target.value)}
                              className="w-full accent-rose-500 cursor-pointer"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-between text-xs px-1 text-zinc-400">
                        <span>Total Progres Otomatis:</span>
                        <span className="text-base font-bold text-white font-mono">
                          {p.totalProgress}%
                        </span>
                      </div>
                    </div>

                    {/* Founder Note */}
                    <div>
                      <label className="text-xs font-semibold text-zinc-300 block mb-1">
                        Catatan Singkat Solo Dev (Pesan Founder):
                      </label>
                      <textarea
                        rows={2}
                        value={p.founderNote}
                        onChange={(e) => handleUpdateEditingProject(pIdx, "founderNote", e.target.value)}
                        placeholder="Contoh: Sedang uji font bahasa Indonesia dan patch scene ending..."
                        className="w-full px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs focus:outline-none focus:border-rose-500 resize-none"
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddProject}
                  className="w-full py-3 rounded-2xl border-2 border-dashed border-zinc-800 hover:border-rose-500/50 text-zinc-400 hover:text-rose-300 text-xs font-bold flex items-center justify-center gap-2 transition"
                >
                  <Plus className="w-4 h-4" />
                  <span>+ Tambah Game Garapan Baru</span>
                </button>
              </div>

              {/* Modal Sticky Footer (Always Pinned and Visible at Bottom) */}
              <div className="flex items-center justify-between p-4 border-t border-zinc-800 bg-zinc-900/95 shrink-0 z-20">
                <button
                  type="button"
                  onClick={handleResetDefault}
                  className="text-xs text-zinc-500 hover:text-zinc-300 underline"
                >
                  Reset Bawaan
                </button>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold shadow-lg shadow-rose-950/50 transition active:scale-95"
                  >
                    <Save className="w-4 h-4" />
                    <span>Simpan Perubahan</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ,
        document.body
      )}
    </section>
  );
}
